// src/oura/components/fixes/useFixesData.ts
//
// The two reads Fixes makes beyond the plan itself: the asks a published
// pack is waiting on the client for, and what the platform logged about
// finished work.

import { useEffect, useRef, useState } from "react";
import { getPlanActivity } from "@service/aeo";
import type { AeoActivityEntry } from "@service/aeo/types";
import { isClientReviewLive, listPacks } from "@service/aeo/clientReview";
import { isOuraProd } from "../../role";

/**
 * Whether the Client screen and its asks exist here: where the approval layer
 * is real, or plainly a preview. Production would otherwise read asks from the
 * in-memory mock and group real work under invented ones, or link to a Client
 * tab that is not there. The same gate the Client tab sits behind.
 */
export const clientAsksShown = isClientReviewLive || !isOuraProd();

/** The asks a published pack is waiting on the client for. */
export interface ClientAsks {
    /** How many: the number Overview and Client count as asks out. */
    count: number;
    /** The tasks they are about, which Fixes groups under Needs the client. */
    taskIds: ReadonlySet<string>;
}

/**
 * The asks waiting on the client, or null while they load, when the read
 * fails, and where `clientAsksShown` is false. Null is not "no asks": a caller
 * says nothing about asks rather than claim there are none, and groups by the
 * task alone.
 *
 * `settled` tells the wait apart from the other two: false only while the
 * read is out. A deep link waits for it, because an ask moves a row to Needs
 * the client, and the link opens the group the row sits in.
 */
export function useClientAsks(businessId: string): { asks: ClientAsks | null; settled: boolean } {
    // Keyed by business, so the first render for a new one does not read the last one's answer as settled.
    const [read, setRead] = useState<{ businessId: string; asks: ClientAsks | null } | null>(null);
    useEffect(() => {
        setRead(null);
        if (!clientAsksShown) return;
        let cancelled = false;
        listPacks(businessId)
            .then((packs) => {
                if (cancelled) return;
                const waiting = packs
                    .filter((p) => p.status !== "draft")
                    .flatMap((p) => p.items)
                    .filter((i) => i.waitingOn === "client");
                setRead({ businessId, asks: { count: waiting.length, taskIds: new Set(waiting.map((i) => i.taskId)) } });
            })
            .catch(() => {
                if (!cancelled) setRead({ businessId, asks: null });
            });
        return () => {
            cancelled = true;
        };
    }, [businessId]);
    const current = read?.businessId === businessId ? read : null;
    return { asks: current?.asks ?? null, settled: !clientAsksShown || current !== null };
}

/**
 * The plan's activity log, read the first time `wanted` turns true and kept
 * for the visit: the fix drawer needs it for the impact line, and a status
 * change does not move what a past run recorded.
 *
 * Null until it arrives, when it fails, or with no assessment (a fixture
 * business), all of which the drawer reads as "nothing recorded".
 */
export function usePlanActivity(assessmentId: string | undefined, wanted: boolean): AeoActivityEntry[] | null {
    const [entries, setEntries] = useState<AeoActivityEntry[] | null>(null);
    // The assessment last asked for. Not cancelled when `wanted` turns false:
    // closing the drawer mid-request must not throw away the answer.
    const requested = useRef<string | null>(null);
    useEffect(() => {
        if (!wanted || !assessmentId || requested.current === assessmentId) return;
        requested.current = assessmentId;
        void getPlanActivity(assessmentId).then((rows) => {
            if (requested.current === assessmentId) setEntries(rows);
        });
    }, [wanted, assessmentId]);
    return entries;
}
